import { useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, EmptyState, QueryStateBoundary, Skeleton } from "@/components/ui";
import { useContact } from "@/api/hooks/useContacts";
import { useMemberships, useMembershipBalance } from "@/api/hooks/useMemberships";
import { MEMBERSHIP_STATUS_BADGE_VARIANT, MEMBERSHIP_STATUS_LABELS, type Membership } from "@/api/types/membership";
import { formatBalanceSummary } from "./hourMath";
import { MemberFormDialog } from "./MemberFormDialog";
import { MembersNav } from "./MembersNav";

/**
 * Members list — tier + status + this-week hour balance per member.
 * Reuses the existing Contact record (useContact) for the display name;
 * Membership itself only carries contact_id.
 */
export function MembersPage() {
  const [formOpen, setFormOpen] = useState(false);
  const { data: memberships, isLoading, error, refetch } = useMemberships();

  return (
    <div className="p-6 sm:p-8">
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-display text-2xl font-bold text-text-primary">Members</h1>
        <Button onClick={() => setFormOpen(true)} data-testid="add-member-button">
          Add member
        </Button>
      </div>

      <MembersNav />

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        {!memberships || memberships.length === 0 ? (
          <EmptyState
            title="No members yet"
            description="Add a member to start tracking their studio hour bank."
            action={{ label: "Add member", onClick: () => setFormOpen(true) }}
          />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border bg-bg-card">
            <table className="w-full text-sm" data-testid="members-table">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase text-text-muted">
                  <th className="px-4 py-2">Member</th>
                  <th className="px-4 py-2">Tier</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Hours this week</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {memberships.map((m) => (
                  <MemberRow key={m.id} membership={m} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryStateBoundary>

      <MemberFormDialog open={formOpen} onClose={() => setFormOpen(false)} />
    </div>
  );
}

function MemberRow({ membership }: { membership: Membership }) {
  const { data: contact } = useContact(membership.contact_id);
  const { data: balance, isLoading: balanceLoading } = useMembershipBalance(
    membership.status === "active" ? membership.id : undefined,
  );

  return (
    <tr className="hover:bg-bg-hover" data-testid={`member-row-${membership.id}`}>
      <td className="px-4 py-3">
        <Link to={`/members/${membership.id}`} className="font-medium text-text-primary hover:underline">
          {contact?.name ?? "…"}
        </Link>
      </td>
      <td className="px-4 py-3 text-text-secondary">{membership.tier_name}</td>
      <td className="px-4 py-3">
        <Badge variant={MEMBERSHIP_STATUS_BADGE_VARIANT[membership.status]}>
          {MEMBERSHIP_STATUS_LABELS[membership.status]}
        </Badge>
      </td>
      <td className="px-4 py-3 text-text-secondary">
        {membership.status !== "active" ? (
          <span className="text-text-muted">—</span>
        ) : balanceLoading || !balance ? (
          <Skeleton className="h-4 w-24" />
        ) : (
          formatBalanceSummary(balance)
        )}
      </td>
    </tr>
  );
}
